import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, switchMap, tap } from 'rxjs/operators';

export type ItemsLoader<T> = (term: string) => Observable<T[]>;

@Component({
    selector: 'protocol-search-field',
    template: require('./protocol-search-field.component.html'),
    styles: [require('./protocol-search-field.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProtocolSearchFieldComponent<TItem> {
    @Input()
    public label: string;

    @Input()
    public control: FormControl;

    @Input()
    public itemsLoader: ItemsLoader<TItem>;

    @Input()
    public thresholdSearch: number;

    public readonly searchTerm$ = new Subject<string>();
    public readonly isSearching$ = new BehaviorSubject<boolean>(false);
    public readonly selectedItems$ = new BehaviorSubject<TItem[]>([]);
    public readonly items$: Observable<TItem[]>;

    constructor() {
        this.items$ = this.searchTerm$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap(() => this.isSearching$.next(true)),
            switchMap(term =>
                this.loadItems(term).pipe(
                    finalize(() => this.isSearching$.next(false)),
                )),
        );
    }

    public onSearchTermChanged(term: string) {
        this.searchTerm$.next(term);
    }

    public onSelectedItemChange(selectedItems: TItem[]) {
        this.control.setValue(selectedItems.length ? selectedItems[0] : null);
    }

    public clearSelect() {
        this.control.setValue(null);
        this.selectedItems$.next([]);
    }

    private loadItems(term: string): Observable<TItem[]> {
        if (!this.itemsLoader) {
            throw new Error('Pass the itemsLoader');
        }

        if (!term || (this.thresholdSearch && term.length < this.thresholdSearch)) {
            return of([]);
        }

        return this.itemsLoader(term);
    }
}
